import React from 'react';

/*
 * notesDoc — Markdown IS the page. This module turns it into the "Dark Notes"
 * reading layout (see knowledge_hub_theme_guide/), and keeps the look stable
 * while the text changes.
 *
 * Three functions, one contract:
 *
 *   parseNotes(markdown)        → a document: hero + concepts + typed blocks
 *   ensureStyle(doc, prevStyle) → the LOOK: decided once, reused for ever
 *   renderNotes(doc, style)     → React elements (never raw HTML — user input)
 *
 * Why a separate style map. Markdown carries the words and the structure; it
 * cannot say "this flow step is mint and that one is blue", or "this is the
 * winning column". Those choices are made ONCE — the first time a page is
 * saved — and stored per block. Editing the words re-parses the markdown and
 * re-applies the SAME decisions, so text updates never repaint the page.
 * A block keeps its decoration as long as its kind and its position among
 * blocks of that kind are unchanged; genuinely new blocks get decorated by the
 * same rules that ran the first time.
 *
 * Authoring conventions (all optional — plain markdown renders fine):
 *   # Title            the page title; the paragraph under it is the lead
 *   ## Concept         a numbered concept block
 *   ### Sub-label      a small blue caps label inside a concept
 *   > [!TIP] text      tip · [!WARN] warning · [!SAY] Label: what to say
 *   > text             a callout; its colour is chosen on the first save
 *   => text            the one-line definition (serif, blue bar)
 *   ```formula         a centred formula block
 *   ```flow            A -> B -> C  (one step per line, or one line)
 *   ```tokens          label, then pieces separated by |
 *   ```py path.py      a code block with a label
 *   ? question         a Q&A item; the paragraph under it is the answer
 *   | a | b |          a table · ![alt](src) an image · - / 1. lists
 */

let seq = 0;
const k = () => `nd-${seq += 1}`;

export const slugify = (s, fallback = 'x') => (String(s || '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  .slice(0, 40) || fallback);

/* ── inline: **bold** *it* `code` [a](b) ~sub~ ^sup^ ─────────────────────── */
const INLINE = /(\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|~([^~\s]+)~|\^([^^\s]+)\^)/g;

export function inline(text) {
  const out = [];
  let last = 0;
  let m;
  const src = String(text == null ? '' : text);
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(src)) !== null) {
    if (m.index > last) out.push(src.slice(last, m.index));
    if (m[2] !== undefined) out.push(<b key={k()}>{m[2]}</b>);
    else if (m[3] !== undefined) out.push(<i key={k()}>{m[3]}</i>);
    else if (m[4] !== undefined) out.push(<code key={k()}>{m[4]}</code>);
    else if (m[5] !== undefined) {
      const href = m[6];
      const ext = /^https?:/i.test(href);
      out.push(<a key={k()} href={href} {...(ext ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{m[5]}</a>);
    } else if (m[7] !== undefined) out.push(<sub key={k()}>{m[7]}</sub>);
    else if (m[8] !== undefined) out.push(<sup key={k()}>{m[8]}</sup>);
    last = m.index + m[0].length;
  }
  if (last < src.length) out.push(src.slice(last));
  return out;
}

/* ── parse ───────────────────────────────────────────────────────────────── */
const TABLE = /^\s*\|.*\|\s*$/;
const RULE = /^\s*[-*_]{3,}\s*$/;
const BULLET = /^\s*[-*+]\s+/;
const ORDERED = /^\s*\d+[.)]\s+/;
const FENCE = /^\s*```\s*(.*)$/;
const CALLOUT = /^\s*>\s?(.*)$/;
const ADMON = /^\s*\[!(TIP|WARN|WARNING|NOTE|SAY|INFO)\]\s*(.*)$/i;

const splitRow = (l) => l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
const isDivider = (cells) => cells.length > 0 && cells.every((c) => /^:?-{2,}:?$/.test(c));

function parseFlow(body) {
  // "A -> B -> C" on one line, or one step per line; "+" joins parts.
  const raw = body.split('\n').map((l) => l.trim()).filter(Boolean);
  const sep = raw.join(' ').includes(' + ') && !raw.join(' ').includes('->') ? '+' : '→';
  let parts = [];
  if (raw.length === 1) parts = raw[0].split(/\s*(?:->|→|\+)\s*/).filter(Boolean);
  else parts = raw;
  let loop = '';
  const nodes = parts.map((p) => {
    let label = p;
    const loopM = label.match(/\s*(↺|\(loop\)|\[loop\])\s*(.*)$/i);
    if (loopM) { loop = loopM[2] ? `↺ ${loopM[2]}` : '↺ repeat'; label = label.slice(0, loopM.index).trim(); }
    const [main, sub] = label.split(/\s*::\s*/);
    return { label: main.trim(), sub: (sub || '').trim() };
  }).filter((n) => n.label);
  return { nodes, sep, loop };
}

function parseTokens(body) {
  const lines = body.split('\n').map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { label: '', items: [] };
  const hasLabel = lines.length > 1 || !lines[0].includes('|');
  const label = hasLabel ? lines[0] : '';
  const rest = (hasLabel ? lines.slice(1) : lines).join(' ');
  return { label, items: rest.split('|').map((t) => t.replace(/^"|"$/g, '')).filter((t) => t !== '') };
}

/** Markdown → { title, lead, concepts:[{ id, title, blocks }] }. */
export function parseNotes(markdown) {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const doc = { title: '', lead: '', concepts: [] };
  let concept = null;
  const ordinals = new Map();

  const openConcept = (title) => {
    const base = slugify(title, `part-${doc.concepts.length + 1}`);
    const taken = new Set(doc.concepts.map((c) => c.id));
    let id = base;
    let n = 2;
    while (taken.has(id)) { id = `${base}-${n}`; n += 1; }
    concept = { id, title: title.trim(), blocks: [] };
    doc.concepts.push(concept);
    ordinals.set(id, new Map());
  };

  const push = (block) => {
    if (!concept) openConcept('Notes');
    const per = ordinals.get(concept.id);
    const n = (per.get(block.t) || 0);
    per.set(block.t, n + 1);
    concept.blocks.push({ ...block, key: `${concept.id}:${block.t}:${n}` });
  };

  let i = 0;
  const flushPara = (buf) => {
    const text = buf.join('\n').trim();
    if (!text) return;
    if (/^=>\s*/.test(text)) { push({ t: 'oneline', text: text.replace(/^=>\s*/, '') }); return; }
    push({ t: 'p', text });
  };

  let para = [];
  const endPara = () => { flushPara(para); para = []; };

  while (i < lines.length) {
    const line = lines[i];

    // fenced blocks
    const fence = line.match(FENCE);
    if (fence) {
      endPara();
      const info = (fence[1] || '').trim();
      const body = [];
      i += 1;
      while (i < lines.length && !FENCE.test(lines[i])) { body.push(lines[i]); i += 1; }
      i += 1;                                     // closing fence
      const text = body.join('\n');
      const kind = info.split(/\s+/)[0].toLowerCase();
      if (kind === 'formula') push({ t: 'formula', text: text.trim() });
      else if (kind === 'flow') push({ t: 'flow', ...parseFlow(text) });
      else if (kind === 'tokens') push({ t: 'tokens', ...parseTokens(text) });
      else push({ t: 'code', label: info.includes(' ') ? info.slice(info.indexOf(' ') + 1).trim() : '', code: text.replace(/\n+$/, '') });
      continue;
    }

    // headings
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      endPara();
      const level = h[1].length;
      const text = h[2].trim();
      if (level === 1 && !doc.title && !doc.concepts.length) { doc.title = text; i += 1; continue; }
      if (level <= 2) openConcept(text);
      else push({ t: 'h3', text });
      i += 1;
      continue;
    }

    // callouts (consecutive "> " lines)
    if (CALLOUT.test(line)) {
      endPara();
      const buf = [];
      while (i < lines.length && CALLOUT.test(lines[i])) { buf.push(lines[i].match(CALLOUT)[1]); i += 1; }
      const joined = buf.join('\n').trim();
      const adm = joined.match(ADMON);
      if (adm) {
        const tag = adm[1].toUpperCase();
        const rest = joined.slice(adm[0].length - adm[2].length).trim();
        const kind = tag === 'WARN' || tag === 'WARNING' ? 'warn' : tag === 'SAY' ? 'say' : 'tip';
        const sep = rest.indexOf(':');
        const hasLabel = sep > 0 && sep < 40;
        push({
          t: 'callout',
          fixed: kind,
          label: hasLabel ? rest.slice(0, sep).trim() : '',
          text: hasLabel ? rest.slice(sep + 1).trim() : rest,
        });
      } else {
        const sep = joined.indexOf(':');
        const hasLabel = sep > 0 && sep < 40 && !joined.slice(0, sep).includes('\n');
        push({
          t: 'callout',
          label: hasLabel ? joined.slice(0, sep).trim() : '',
          text: hasLabel ? joined.slice(sep + 1).trim() : joined,
        });
      }
      continue;
    }

    // Q&A: "? question" then the following paragraph is the answer
    if (/^\s*\?\s+\S/.test(line)) {
      endPara();
      const items = [];
      while (i < lines.length && /^\s*\?\s+\S/.test(lines[i])) {
        const q = lines[i].replace(/^\s*\?\s+/, '').trim();
        i += 1;
        const ans = [];
        while (i < lines.length && lines[i].trim() !== '' && !/^\s*\?\s+\S/.test(lines[i])) { ans.push(lines[i].trim()); i += 1; }
        while (i < lines.length && lines[i].trim() === '' && /^\s*\?\s+\S/.test(lines[i + 1] || '')) i += 1;
        items.push({ q, a: ans.join(' ') });
      }
      push({ t: 'qa', items });
      continue;
    }

    // tables
    if (TABLE.test(line)) {
      endPara();
      const run = [];
      while (i < lines.length && TABLE.test(lines[i])) { run.push(lines[i]); i += 1; }
      const rows = run.map(splitRow);
      let head = null;
      let body = rows;
      if (rows.length >= 2 && isDivider(rows[1])) { head = rows[0]; body = rows.slice(2); }
      push({ t: 'table', head, rows: body });
      continue;
    }

    // lists
    if (BULLET.test(line) || ORDERED.test(line)) {
      endPara();
      const ordered = ORDERED.test(line);
      const items = [];
      while (i < lines.length && (ordered ? ORDERED.test(lines[i]) : BULLET.test(lines[i]))) {
        items.push(lines[i].replace(ordered ? ORDERED : BULLET, '').trim());
        i += 1;
        // continuation lines belong to the item above
        while (i < lines.length && lines[i].trim() && !BULLET.test(lines[i]) && !ORDERED.test(lines[i])
               && /^\s{2,}\S/.test(lines[i])) { items[items.length - 1] += ` ${lines[i].trim()}`; i += 1; }
      }
      push({ t: ordered ? 'ol' : 'ul', items });
      continue;
    }

    // image on its own line
    const img = line.trim().match(/^!\[([^\]]*)\]\(([^)\s]+)\)$/);
    if (img) { endPara(); push({ t: 'figure', alt: img[1], src: img[2] }); i += 1; continue; }

    if (RULE.test(line)) { endPara(); i += 1; continue; }       // horizontal rules are dropped

    if (line.trim() === '') { endPara(); i += 1; continue; }

    para.push(line);
    i += 1;
  }
  endPara();

  // The lead: the first paragraph of the document, promoted out of its concept.
  const first = doc.concepts[0];
  if (first && first.blocks.length && first.blocks[0].t === 'p'
      && (!first.title || first.title === 'Notes' || doc.title)) {
    if (!doc.title && first.title && first.title !== 'Notes') doc.title = first.title;
    if (first.title === 'Notes' || doc.title === first.title) {
      doc.lead = first.blocks[0].text;
      first.blocks.shift();
      if (!first.blocks.length) doc.concepts.shift();
    }
  }
  return doc;
}

/* ── the look: decided once, then held ───────────────────────────────────── */
const WARN_WORDS = /\b(warn|warning|watch out|careful|caution|avoid|don'?t|never|pitfall|mistake|gotcha|breaks?|fails?)\b/i;
const SAY_WORDS = /\b(say|interview|in one sentence|out loud|answer|explain it|senior|takeaway)\b/i;
const WIN_WORDS = /\b(best|winner|recommended|preferred|practical balance|the default|✓|✔)\b/i;

const calloutKind = (b) => {
  const hay = `${b.label} ${b.text}`;
  if (WARN_WORDS.test(hay)) return 'warn';
  if (SAY_WORDS.test(`${b.label}`) || /^say it/i.test(b.label)) return 'say';
  return 'tip';
};

const flowAccents = (n) => Array.from({ length: n }, (_, j) => {
  if (n === 1) return 'n3';
  if (j === 0 || j === n - 1) return 'n3';          // mint: where it starts and ends
  if (j === Math.floor((n - 1) / 2)) return 'n4';   // blue: the middle step
  return '';
});

/**
 * The decorations markdown can't express. Runs over every block; blocks that
 * already have an entry keep it, so editing the words changes nothing here.
 */
export function ensureStyle(doc, prev) {
  const before = (prev && prev.blocks) || {};
  const blocks = {};
  let bigFormulaTaken = Object.values(before).some((d) => d && d.big);
  doc.concepts.forEach((c) => {
    c.blocks.forEach((b) => {
      const kept = before[b.key];
      if (kept) { blocks[b.key] = kept; return; }
      if (b.t === 'callout' && !b.fixed) blocks[b.key] = { kind: calloutKind(b) };
      else if (b.t === 'flow') blocks[b.key] = { accents: flowAccents(b.nodes.length), loop: b.loop || '' };
      else if (b.t === 'tokens') blocks[b.key] = { shift: 0 };
      else if (b.t === 'formula') { blocks[b.key] = { big: !bigFormulaTaken }; bigFormulaTaken = true; }
      else if (b.t === 'table') {
        let win = null;
        (b.rows || []).forEach((row, r) => row.forEach((cell, col) => {
          if (!win && col > 0 && WIN_WORDS.test(cell)) win = { r, c: col };
        }));
        if (win) blocks[b.key] = { win };
      }
    });
  });
  return {
    v: 1,
    blocks,
    generatedAt: (prev && prev.generatedAt) || new Date().toISOString(),
  };
}

/** True when this markdown has never been styled (first save generates it). */
export const isFirstStyling = (style) => !style || !style.blocks || !Object.keys(style.blocks).length;

/* ── render ──────────────────────────────────────────────────────────────── */
const Block = ({ b, dec }) => {
  switch (b.t) {
    case 'p':
      return <p>{inline(b.text)}</p>;
    case 'oneline':
      return <p className="oneline">{inline(b.text)}</p>;
    case 'h3':
      return <h3>{inline(b.text)}</h3>;
    case 'ul':
      return <ul>{b.items.map((it) => <li key={k()}>{inline(it)}</li>)}</ul>;
    case 'ol':
      return <ol>{b.items.map((it) => <li key={k()}>{inline(it)}</li>)}</ol>;
    case 'callout': {
      const kind = b.fixed || (dec && dec.kind) || 'tip';
      const cls = kind === 'say' ? 'say' : kind === 'warn' ? 'tip warn' : 'tip';
      return (
        <div className={cls}>
          {b.label && <b>{b.label}</b>}
          {b.label ? ' ' : null}
          {inline(b.text)}
        </div>
      );
    }
    case 'formula':
      return <div className={`formula${dec && dec.big ? ' big' : ''}`}>{inline(b.text)}</div>;
    case 'code':
      return (
        <div className="code-wrap">
          {b.label && <div className="code-label">{b.label}</div>}
          <pre><code>{b.code}</code></pre>
        </div>
      );
    case 'table':
      return (
        <div className="tablewrap">
          <table>
            {b.head && <thead><tr>{b.head.map((c) => <th key={k()}>{inline(c)}</th>)}</tr></thead>}
            <tbody>
              {b.rows.map((row, r) => (
                <tr key={k()}>
                  {row.map((cell, c) => (
                    <td key={k()} className={dec && dec.win && dec.win.r === r && dec.win.c === c ? 'win' : undefined}>
                      {inline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'flow': {
      const accents = (dec && dec.accents) || [];
      const loop = (dec && dec.loop) || b.loop;
      return (
        <div className="flow">
          {b.nodes.map((n, j) => (
            <React.Fragment key={k()}>
              {j > 0 && <span className="arr">{b.sep}</span>}
              <span className={`node ${accents[j] || ''}`.trim()}>
                {n.label}
                {n.sub && <small>{n.sub}</small>}
              </span>
            </React.Fragment>
          ))}
          {loop && <span className="loop">{loop}</span>}
        </div>
      );
    }
    case 'tokens': {
      const shift = (dec && dec.shift) || 0;
      return (
        <div className="toks">
          {b.label && <span className="lbl">{b.label}</span>}
          {b.items.map((t, j) => <span key={k()} className={`tok c${((j + shift) % 5) + 1}`}>{t}</span>)}
        </div>
      );
    }
    case 'qa':
      return (
        <>
          {b.items.map((it) => (
            <details className="qa" key={k()}>
              <summary>{inline(it.q)}</summary>
              {it.a && <div className="ans">{inline(it.a)}</div>}
            </details>
          ))}
        </>
      );
    case 'figure':
      return (
        <figure className="dg">
          <img src={b.src} alt={b.alt} />
          {b.alt && <figcaption>{b.alt}</figcaption>}
        </figure>
      );
    default:
      return null;
  }
};

/** The concepts of a parsed document, in the Dark Notes layout. */
export function renderNotes(doc, style) {
  const map = (style && style.blocks) || {};
  return doc.concepts.map((c, idx) => (
    <section className="concept" id={c.id} key={c.id}>
      <div className="concept-header">
        <span className="concept-num">{String(idx + 1).padStart(2, '0')}</span>
        <h2>{inline(c.title)}</h2>
      </div>
      <div className="concept-accent" />
      <div className="concept-body">
        {c.blocks.map((b) => <Block key={b.key} b={b} dec={map[b.key]} />)}
      </div>
    </section>
  ));
}

/* ── notes as editable sections ───────────────────────────────────────────
 * The page is one markdown document, but it is AUTHORED as notes: a title, a
 * lead, then one note per `## heading`. These two functions are the bridge —
 * split for the editor, join for storage — and they round-trip losslessly, so
 * the block keys (and the look they carry) survive editing.
 */
export function splitSections(markdown) {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const head = [];
  const sections = [];
  let cur = null;
  let fenced = false;
  lines.forEach((line) => {
    if (/^\s*```/.test(line)) fenced = !fenced;
    const h = !fenced && line.match(/^##\s+(.*)$/);
    if (h) { cur = { heading: h[1].trim(), body: [] }; sections.push(cur); return; }
    (cur ? cur.body : head).push(line);
  });
  const headText = head.join('\n');
  const titleM = headText.match(/^\s*#\s+(.*)$/m);
  const lead = headText.replace(/^\s*#\s+.*$/m, '').trim();
  return {
    title: titleM ? titleM[1].trim() : '',
    lead,
    sections: sections.map((x) => ({ heading: x.heading, body: x.body.join('\n').trim() })),
  };
}

export function joinSections({ title, lead, sections }) {
  const out = [];
  if (title && title.trim()) out.push(`# ${title.trim()}`, '');
  if (lead && lead.trim()) out.push(lead.trim(), '');
  (sections || []).forEach((s) => {
    if (!s.heading.trim() && !s.body.trim()) return;
    out.push(`## ${s.heading.trim() || 'Untitled note'}`, '');
    if (s.body.trim()) out.push(s.body.trim(), '');
  });
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/** Legacy pages ({intro, tabs}) → markdown, so any page can open in the editor. */
export function docToMarkdown(doc, name) {
  const out = [];
  if (name) out.push(`# ${name}`, '');
  if (doc && doc.intro) out.push(doc.intro, '');
  (doc?.tabs || []).forEach((t) => {
    out.push(`## ${t.label}`, '');
    if (t.md) { out.push(t.md.trim(), ''); return; }
    (t.points || []).forEach((p) => {
      out.push(typeof p === 'string' ? `- ${p}` : `- **${p.t}** — ${p.d}`);
    });
    out.push('');
  });
  return out.join('\n').trim();
}
